/**
 * 页面刷新处理工具
 * 用于处理页面刷新后的状态恢复和错误处理
 */
import { getToken, isValidToken, getTenantId } from './token.js';

const isDev = import.meta.env?.DEV;
let handlerInitialized = false;
let restoreInFlight = null;
let restoredThisSession = false;

const devLog = (...args) => {
  if (isDev) console.log(...args);
};

// 检查页面是否是通过刷新加载的
export const isPageRefresh = () => {
  // 优先使用 Navigation Timing Level 2
  try {
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    if (nav && nav.type) {
      return nav.type === 'reload';
    }
  } catch (_) {
    // ignore
  }
  // 兼容旧 API
  return !!(window.performance && window.performance.navigation && window.performance.navigation.type === 1);
};

// 检查页面加载状态（纯计算，无副作用）
export const checkPageLoadStatus = () => {
  const status = {
    isRefreshed: isPageRefresh(),
    hasUserData: !!localStorage.getItem('user'),
    hasToken: isValidToken(getToken()),
    currentPath: window.location.pathname,
    timestamp: Date.now()
  };
  return status;
};

/**
 * 恢复页面状态（会话内只真正执行一次，避免 load / PageLoader / StrictMode 重复）
 * @param {{ force?: boolean }} options
 */
export const restorePageState = async (options = {}) => {
  const { force = false } = options;

  if (!force && restoredThisSession) {
    return {
      success: true,
      message: '本会话已恢复过状态',
      status: checkPageLoadStatus(),
      skipped: true
    };
  }

  if (restoreInFlight) {
    return restoreInFlight;
  }

  restoreInFlight = (async () => {
    try {
      const status = checkPageLoadStatus();

      if (status.isRefreshed && status.hasUserData && status.hasToken) {
        const tenantId = typeof localStorage !== 'undefined' ? getTenantId() : null;
        devLog('[驾驶舱·刷新]', { tenantId: tenantId || '(空)', path: status.currentPath });
        restoredThisSession = true;
        return {
          success: true,
          message: '页面状态恢复成功',
          status
        };
      }

      // 非刷新场景也标记，避免后续无意义重复检查
      restoredThisSession = true;
      return {
        success: true,
        message: '无需恢复状态',
        status
      };
    } catch (error) {
      console.error('页面状态恢复失败:', error);
      return {
        success: false,
        message: '页面状态恢复失败',
        error: error.message
      };
    } finally {
      restoreInFlight = null;
    }
  })();

  return restoreInFlight;
};

// 处理页面加载错误
export const handlePageLoadError = (error) => {
  console.error('页面加载错误:', error);

  const errorLog = {
    timestamp: Date.now(),
    error: error.message || '未知错误',
    stack: error.stack,
    url: window.location.href,
    userAgent: navigator.userAgent
  };

  try {
    const existingErrors = JSON.parse(localStorage.getItem('pageErrors') || '[]');
    existingErrors.push(errorLog);

    if (existingErrors.length > 10) {
      existingErrors.splice(0, existingErrors.length - 10);
    }

    localStorage.setItem('pageErrors', JSON.stringify(existingErrors));
  } catch (e) {
    console.error('保存错误日志失败:', e);
  }

  return errorLog;
};

// 清理过期的错误日志
export const cleanupErrorLogs = () => {
  try {
    const errors = JSON.parse(localStorage.getItem('pageErrors') || '[]');
    const now = Date.now();
    const oneDayAgo = now - (24 * 60 * 60 * 1000);

    const validErrors = errors.filter(error => error.timestamp > oneDayAgo);
    if (validErrors.length !== errors.length) {
      localStorage.setItem('pageErrors', JSON.stringify(validErrors));
    }
  } catch (e) {
    console.error('清理错误日志失败:', e);
  }
};

// 获取页面加载性能指标
export const getPageLoadMetrics = () => {
  if (window.performance && window.performance.timing) {
    const timing = window.performance.timing;
    const loadTime = timing.loadEventEnd - timing.navigationStart;
    const domReadyTime = timing.domContentLoadedEventEnd - timing.navigationStart;

    return {
      loadTime,
      domReadyTime,
      navigationStart: timing.navigationStart,
      loadEventEnd: timing.loadEventEnd
    };
  }

  return null;
};

/**
 * 前台可见时轻量校验：本地已有 user + 有效 token 则跳过 restore
 */
const onVisibilityVisible = () => {
  if (document.visibilityState !== 'visible') return;

  const hasUserData = !!localStorage.getItem('user');
  const hasToken = isValidToken(getToken());
  if (hasUserData && hasToken) {
    return;
  }

  // 登录态缺失时才尝试恢复（例如其它标签页登出后回到本页）
  restorePageState({ force: true });
};

// 初始化页面刷新处理器（幂等，避免 HMR / 重复 import 多次挂监听）
export const initPageRefreshHandler = () => {
  if (handlerInitialized) return;
  handlerInitialized = true;

  cleanupErrorLogs();

  document.addEventListener('visibilitychange', onVisibilityVisible);

  // 首次加载恢复交给 PageLoader / UserContext；此处仅在 load 时做一次幂等尝试
  window.addEventListener('load', () => {
    restorePageState();
  });
};
